import React, { useEffect, useMemo, useRef } from 'react';
import { TypesenseProduct } from '@Types/typesense';
import { Hit } from 'instantsearch.js';
import { useInfiniteHits, useInstantSearch } from 'react-instantsearch';
import { ProductGrid, ProductItem, ProductList, ProductView } from 'components/tailgrids-ui/products/product-list';
import { newCategoryId, OpenedState, saleCategoryId } from 'features/typesense/components';
import { mapLocalizedString, TYPESENSE_LABEL_PREFIX } from 'features/typesense/utils';
import { useFormat } from 'helpers/hooks/useFormat';

type HitsProps = {
  locale?: string;
  priceCountry: string;
  priceGroup: string;
  view?: ProductView;
  showMobile?: OpenedState;
} & Partial<React.HTMLAttributes<HTMLDivElement>>;

export type GroupedTypesenseProductHits = {
  [product_id: string]: Hit<TypesenseProduct>[];
};

export const groupVariants = (items: Hit<TypesenseProduct>[]) => {
  return items.reduce(
    (grouped, element) => ({
      ...grouped,
      [element?.product_id]: [...(grouped[element?.product_id] || []), element],
    }),
    {} as GroupedTypesenseProductHits,
  );
};

export const SCROLL_TOP_ELEMENT = 'topHitsElement';

const filterTags = (category_ids: string[]) => {
  return [
    category_ids?.includes(saleCategoryId) ? 'sale' : '',
    category_ids?.includes(newCategoryId) ? 'new' : '',
  ].filter((tag) => tag.length > 0);
};

export const Hits = (props: HitsProps) => {
  const { locale, view, priceCountry, priceGroup, showMobile, ...divProps } = props;
  const { status } = useInstantSearch();
  const { showMore, isLastPage, hits } = useInfiniteHits<TypesenseProduct>({ showPrevious: true });
  const sentinelRef = useRef(null);
  const { formatMessage } = useFormat({ name: 'product' });

  const groupedHits = useMemo(() => {
    return groupVariants(hits);
  }, [hits]);
  const loading = status === 'stalled' || status === 'loading';
  const show = useMemo(() => loading || (!loading && Object.keys(groupedHits).length === 0), [groupedHits, loading]);

  const productItems = useMemo(() => {
    return Object.keys(groupedHits).map((productId) => {
      const variants = groupedHits[productId];
      return {
        id: productId,
        disabled: showMobile !== 'none',
        variants: variants?.map((variant) => {
          return {
            sku: variant?.sku,
            images: variant?.image_urls,
            href: `/${mapLocalizedString(variant?.slugs, locale)}/p/${variant?.sku}`,
            title: mapLocalizedString(variant?.names, locale),
            description: mapLocalizedString(variant?.descriptions, locale),
            priceCentAmount: variant?.price?.[priceCountry]?.[priceGroup]?.cent_amount,
            discountedCentAmount: variant?.price?.[priceCountry]?.[priceGroup]?.discounted?.cent_amount,
            attributes: {
              color: variant?.attributes?.color,
              size: variant?.attributes?.size,
              sizeLabel: TYPESENSE_LABEL_PREFIX + 'attributes.size.' + variant?.attributes?.size,
              inStock: variant?.in_stock,
            },
            tags: filterTags(variant?.category_ids),
          };
        }),
        labelPrefix: TYPESENSE_LABEL_PREFIX,
      } as ProductItem;
    });
  }, [groupedHits, showMobile, locale, priceCountry, priceGroup]);

  useEffect(() => {
    if (sentinelRef.current !== null) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isLastPage && !loading) {
            showMore();
          }
        });
      });

      observer.observe(sentinelRef.current);

      return () => {
        observer.disconnect();
      };
    }
  }, [isLastPage, loading, showMore]);

  return (
    <div {...divProps}>
      <div id={SCROLL_TOP_ELEMENT} className="h-0 w-0"></div>
      {view === 'grid' ? (
        <ProductGrid locale={locale} products={productItems} loading={loading} />
      ) : (
        <ProductList locale={locale} products={productItems} />
      )}
      <div ref={sentinelRef} aria-hidden="true" />
      {/* <div className={'flex w-full justify-center px-6 pb-6 ' + (show ? '' : 'hidden')}>
        <div role="status" className={loading ? '' : 'hidden'}>
          <svg
            aria-hidden="true"
            className="mr-2 h-8 w-8 animate-spin fill-blue-600 text-gray-200 dark:text-gray-600"
            viewBox="0 0 100 101"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentFill"
            />
          </svg>
          <span className="sr-only">Loading...</span>
        </div>
        <div className={!loading && Object.keys(groupedHits).length === 0 ? '' : 'hidden'}>
          {formatMessage({ id: 'noProductsFound', defaultMessage: 'No products found.' })}
        </div>
      </div> */}
    </div>
  );
};
